import React from 'react';
import CopyButton from './CopyButton';

// Turns Claude's markdown-ish replies into React elements. Every piece of text goes through React
// as a text node, so nothing here can inject HTML (no raw-HTML props, no DOM writes).
// The text may be mid-stream, so unclosed fences and markers render as what they are (an open
// fence shows the rest as code, a lone ** or ` shows literally); nothing is swallowed or thrown.

const FENCE_OPEN = /^\s*```\s*([^`\s]*)[^`]*$/;
const FENCE_CLOSE = /^\s*```\s*$/;
const HEADING = /^(#{1,6})\s+(.*)$/;
const LIST_ITEM = /^([ \t]*)([-*+]|\d{1,9}[.)])\s+(.*)$/;
// Label and URL are length-capped so a line full of '[' cannot make the scan quadratic. The URL
// may hold one level of balanced parentheses (Wikipedia-style links).
const LINK = /\[([^\]\n]{1,500})\]\(((?:[^()\s]|\([^()\s]{0,500}\)){1,2000})\)/y;
const SAFE_URL = /^(https?:\/\/|mailto:)/i;
const SPACE = /\s/;

function CodeBlock({ code, lang }) {
  return (
    <div className="cc-md-codeblock">
      <div className="cc-md-codehead">
        <span className="cc-md-lang">{lang}</span>
        <CopyButton text={code} />
      </div>
      <pre className="cc-md-pre"><code>{code}</code></pre>
    </div>
  );
}

// Inline formatting for one line: `code`, **bold**, *italic*, [text](url).
function inline(s, kp) {
  const out = [];
  let buf = '';
  let k = 0;
  const flush = () => { if (buf) { out.push(buf); buf = ''; } };
  const push = (el) => { flush(); out.push(el); };
  // Once a closing marker is missing, it is missing for the rest of the line too.
  let noTick = false;
  let noBold = false;
  let noItal = false;
  const n = s.length;
  let i = 0;
  while (i < n) {
    const c = s[i];
    if (c === '`' && !noTick) {
      const j = s.indexOf('`', i + 1);
      if (j === -1) { noTick = true; buf += c; i += 1; continue; }
      if (j === i + 1) { buf += '``'; i += 2; continue; }
      push(<code key={`${kp}c${k++}`} className="cc-md-code">{s.slice(i + 1, j)}</code>);
      i = j + 1;
      continue;
    }
    if (c === '*' && s[i + 1] === '*') {
      const j = noBold ? -1 : s.indexOf('**', i + 2);
      if (j === -1) noBold = true;
      if (j === -1 || j === i + 2) { buf += '**'; i += 2; continue; }
      push(<strong key={`${kp}b${k++}`}>{inline(s.slice(i + 2, j), `${kp}b${k}-`)}</strong>);
      i = j + 2;
      continue;
    }
    if (c === '*' && !noItal && i + 1 < n && !SPACE.test(s[i + 1])) {
      const j = s.indexOf('*', i + 1);
      if (j === -1) { noItal = true; buf += c; i += 1; continue; }
      if (SPACE.test(s[j - 1])) { buf += c; i += 1; continue; }
      push(<em key={`${kp}i${k++}`}>{inline(s.slice(i + 1, j), `${kp}i${k}-`)}</em>);
      i = j + 1;
      continue;
    }
    if (c === '[') {
      LINK.lastIndex = i;
      const m = LINK.exec(s);
      if (m) {
        if (SAFE_URL.test(m[2])) {
          push(<a key={`${kp}a${k++}`} href={m[2]} target="_blank" rel="noopener noreferrer">{inline(m[1], `${kp}a${k}-`)}</a>);
        } else {
          buf += m[0]; // any other scheme (javascript:, data:, relative) stays plain text
        }
        i += m[0].length;
        continue;
      }
    }
    buf += c;
    i += 1;
  }
  flush();
  return out;
}

const indentOf = (ws) => ws.replace(/\t/g, '    ').length;

// items: [{ indent, ordered, num, text }]; builds nested lists from indentation.
function buildList(items, start, kp) {
  const first = items[start];
  const base = first.indent;
  const lis = [];
  let i = start;
  while (i < items.length) {
    const it = items[i];
    if (it.indent < base) break;
    if (it.indent === base || it.indent < base + 2) {
      if (it.ordered !== first.ordered) break;
      lis.push({ key: `${kp}l${i}`, content: inline(it.text, `${kp}l${i}-`), children: [] });
      i += 1;
    } else {
      // Deeper: a nested list under the previous item.
      const nested = buildList(items, i, `${kp}n${i}-`);
      lis[lis.length - 1].children.push(nested.node);
      i = nested.next;
    }
  }
  const Tag = first.ordered ? 'ol' : 'ul';
  const startAttr = first.ordered && first.num !== 1 ? { start: first.num } : {};
  const node = (
    <Tag key={kp} className="cc-md-list" {...startAttr}>
      {lis.map((li) => <li key={li.key}>{li.content}{li.children}</li>)}
    </Tag>
  );
  return { node, next: i };
}

export function formatText(text) {
  if (text == null || text === '') return [];
  const lines = String(text).replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let para = [];
  let list = [];
  let b = 0;

  const flushPara = () => {
    if (!para.length) return;
    const kids = [];
    para.forEach((line, idx) => {
      if (idx) kids.push(<br key={`br${idx}`} />);
      kids.push(...inline(line, `p${b}-${idx}-`));
    });
    blocks.push(<p key={`p${b++}`} className="cc-md-p">{kids}</p>);
    para = [];
  };
  const flushList = () => {
    let i = 0;
    while (i < list.length) {
      const r = buildList(list, i, `L${b++}-`);
      blocks.push(r.node);
      i = r.next;
    }
    list = [];
  };
  const flushAll = () => { flushPara(); flushList(); };

  for (let li = 0; li < lines.length; li++) {
    const line = lines[li];
    const fence = FENCE_OPEN.exec(line);
    if (fence) {
      flushAll();
      const body = [];
      let j = li + 1;
      while (j < lines.length && !FENCE_CLOSE.test(lines[j])) body.push(lines[j++]);
      blocks.push(<CodeBlock key={`c${b++}`} code={body.join('\n')} lang={fence[1]} />);
      li = j; // the closing fence (or the end, when the fence is still open mid-stream)
      continue;
    }
    if (!line.trim()) { flushAll(); continue; }
    const h = HEADING.exec(line);
    if (h) {
      flushAll();
      const level = Math.min(h[1].length, 3);
      const Tag = `h${level + 2}`;
      blocks.push(<Tag key={`h${b++}`} className={`cc-md-h${level}`}>{inline(h[2], `h${b}-`)}</Tag>);
      continue;
    }
    const m = LIST_ITEM.exec(line);
    if (m) {
      flushPara();
      const ordered = /\d/.test(m[2]);
      list.push({ indent: indentOf(m[1]), ordered, num: ordered ? parseInt(m[2], 10) : 0, text: m[3] });
      continue;
    }
    flushList();
    para.push(line);
  }
  flushAll();
  return blocks;
}

export default formatText;
